import { useMutation, useQueryClient } from "@tanstack/react-query";
import { BellOff, Check, ChevronDown, Code2, Handshake, Puzzle, Settings } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { useAlertPrefs } from "@/chat/alerts";
import { FULL_EDITION } from "@/desktop/edition";
import { errorMessage, patch } from "@/lib/api";
import { keys, useMe, useNotifications } from "@/lib/queries";
import type { Group } from "@/lib/types";
import { cn } from "@/lib/utils";
import { AccessRequestActions, InviteActions, notificationText } from "./notifications";
import { Avatar } from "./ui/controls";
import { Menu, MenuContent, MenuItem, MenuLabel, MenuSeparator, MenuTrigger, Tip } from "./ui/overlay";

/** Avatar do grupo: a imagem dele, ou a inicial (preto e branco). */
export function GroupAvatar({
  group,
  size = 20,
  unread = 0,
}: {
  group: Pick<Group, "name"> & { imageUrl?: string | null };
  size?: number;
  unread?: number;
}) {
  return (
    <span
      className="relative flex shrink-0 items-center justify-center rounded-md border border-border-strong bg-bg-emphasis font-bold uppercase text-fg"
      style={{ width: size, height: size, fontSize: Math.max(10, Math.round(size * 0.42)) }}
    >
      {group.imageUrl ? (
        <img src={group.imageUrl} alt="" className="h-full w-full rounded-[inherit] object-cover" />
      ) : (
        group.name.slice(0, 1)
      )}
      {unread ? (
        <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-fg ring-2 ring-bg-subtle" />
      ) : null}
    </span>
  );
}

/** Convites e pedidos esperando resposta: só aparece quando há algo, com o contador. */
export function PendingInvites() {
  const qc = useQueryClient();
  const { data: me } = useMe();
  const { data } = useNotifications(Boolean(me) && !me?.user.isGuest);
  const items = (data?.items ?? []).filter(
    (n) =>
      (n.kind === "direct_invite" && typeof n.data.inviteId === "string") ||
      (n.kind === "access_request" && typeof n.data.projectId === "string"),
  );
  const [open, setOpen] = useState(true);
  if (!items.length) return null;
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: keys.notifications });
    void qc.invalidateQueries({ queryKey: keys.projects });
    void qc.invalidateQueries({ queryKey: keys.groups });
  };
  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 px-2.5 pb-1 text-[11.5px] font-semibold uppercase tracking-[0.06em] text-fg-muted hover:text-fg"
        aria-expanded={open}
      >
        <ChevronDown className={cn("h-3 w-3 transition-transform", !open && "-rotate-90")} />
        Convites pendentes
        <span className="ml-auto rounded-full bg-accent px-1.5 text-[10.5px] font-bold text-accent-fg">
          {items.length}
        </span>
      </button>
      {open
        ? items.slice(0, 4).map((n) => (
            <div
              key={n.id}
              className="rounded-md border border-border bg-bg px-2.5 py-2 text-[12.5px] leading-snug text-fg"
            >
              {notificationText(n)}
              {n.kind === "direct_invite" ? (
                <InviteActions inviteId={n.data.inviteId as string} onDone={refresh} />
              ) : (
                <AccessRequestActions
                  projectId={n.data.projectId as string}
                  userId={(n.data.by as { id: string }).id}
                  onDone={refresh}
                />
              )}
            </div>
          ))
        : null}
    </div>
  );
}

type Status = "coding" | "available" | "dnd";

const STATUS: Record<Status, { label: string; hint: string; dot: string; icon: typeof Code2 }> = {
  coding: {
    label: "Programando",
    hint: "Focado no código; avisos chegam normalmente",
    dot: "var(--fg-muted)",
    icon: Code2,
  },
  available: {
    label: "Disponível para colaborar",
    hint: "Aparece em Descobrir devs e nos projetos como aberto a programar junto",
    dot: "var(--accent)",
    icon: Handshake,
  },
  dnd: {
    label: "Não perturbe",
    hint: "Sem som, notificação ou balão de mensagem",
    dot: "var(--danger)",
    icon: BellOff,
  },
};

/**
 * Você, no rodapé: avatar, nome e o status (Programando, Disponível para colaborar, Não perturbe).
 * "Disponível" vai para o perfil (Descobrir e presença); "Não perturbe" silencia os avisos.
 */
export function ProfileStatus() {
  const qc = useQueryClient();
  const { data: me } = useMe();
  const dnd = useAlertPrefs((s) => s.dnd);
  const setPrefs = useAlertPrefs((s) => s.set);
  const available = me?.profile?.available ?? false;
  const status: Status = dnd ? "dnd" : available ? "available" : "coding";
  const save = useMutation({
    mutationFn: (v: boolean) => patch("/me/profile", { available: v }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.me }),
    onError: (e) => toast.error(errorMessage(e)),
  });
  if (!me || me.user.isGuest) return null;
  const choose = (s: Status) => {
    setPrefs({ dnd: s === "dnd" });
    if ((s === "available") !== available) save.mutate(s === "available");
  };
  return (
    <Menu>
      <MenuTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-bg-muted data-[state=open]:bg-bg-emphasis"
          aria-label={`Seu status: ${STATUS[status].label}`}
        >
          <span className="relative">
            <Avatar name={me.user.name} src={me.user.avatarUrl} size={28} />
            <span
              className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-bg-subtle"
              style={{ backgroundColor: STATUS[status].dot }}
            />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13px] font-medium text-fg">{me.user.name}</span>
            <span className="block truncate text-[11.5px] text-fg-muted">{STATUS[status].label}</span>
          </span>
          <ChevronDown className="h-3.5 w-3.5 text-fg-muted" />
        </button>
      </MenuTrigger>
      <MenuContent side="top" align="start" className="w-72">
        <MenuLabel>Seu status</MenuLabel>
        {(Object.keys(STATUS) as Status[]).map((s) => {
          const meta = STATUS[s];
          return (
            <MenuItem key={s} className="h-auto py-1.5" onSelect={() => choose(s)}>
              <span className="flex flex-1 items-start gap-2">
                <span
                  className="mt-1.5 h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: meta.dot }}
                />
                <span className="min-w-0 flex-1">
                  <span className="block">{meta.label}</span>
                  <span className="block text-[11.5px] text-fg-muted">{meta.hint}</span>
                </span>
                {status === s ? <Check className="mt-0.5 h-3.5 w-3.5" /> : null}
              </span>
            </MenuItem>
          );
        })}
        <MenuSeparator />
        <MenuItem asChild>
          <Link to={`/u/${me.user.handle}`}>Ver meu perfil</Link>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
}

/** Rodapé: o que é de uso ocasional (Plugins, Configurações) sai do espaço nobre. */
export function SidebarFooterLinks({ active }: { active: (to: string) => boolean }) {
  const link = (to: string, label: string, Icon: typeof Puzzle) => (
    <Tip label={label}>
      <Link
        to={to}
        aria-label={label}
        className={cn(
          "flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md text-[12.5px] transition-colors",
          active(to) ? "bg-bg-emphasis text-fg" : "text-fg-muted hover:bg-bg-muted hover:text-fg",
        )}
      >
        <Icon className="h-4 w-4" strokeWidth={1.75} /> {label}
      </Link>
    </Tip>
  );
  return (
    <div className="flex gap-1">
      {FULL_EDITION ? link("/plugins", "Plugins", Puzzle) : null}
      {FULL_EDITION ? link("/settings", "Ajustes", Settings) : null}
    </div>
  );
}
